/**
 * drainFieldQueue (Inventory Screens Step 4.3b + 4.3d-A) — the field-zone
 * replay drain, TYPE-AWARE since 4.3d: the loop reads ALL pending items FIFO
 * and a per-item switch on item.type routes to the right sender:
 *
 *   field_capture → sync_field_capture (single-record envelope; the original
 *                   4.3b path, lifted verbatim into sendFieldCapture)
 *   media_add     → upload_media (base64 JSON; 201 fresh / 200 idempotent
 *                   both mark 'saved' with {media_id, blob_path, blob_url,
 *                   idempotent}; 404 = tool-not-found → terminal 'error',
 *                   NOT a retry)
 *   media_remove  → delete_media (query params; 404 = doc already gone =
 *                   goal achieved → 'saved' {already_absent:true}, D-4.AE)
 *   anything else → defensive terminal 'error', never sent.
 *
 * Items in conflict / error / not_found / failed are review-gated (D-4.J) —
 * the drain never touches them; Step 5 owns that flow. The drain marks
 * outcomes via markCaptureResult and NEVER dequeues, never reconciles —
 * that's reconcileConfirmations (4.3c-i, media branch 4.3d-A).
 *
 * Re-entrancy: module-level latch in drainLatch.js (mirrors useDisposalOfflineQueue
 * :104/:153/:188) — remounts and double-fired triggers share ONE latch.
 *
 * Auth: preflight ONCE per cycle (getFieldTokenSilent). Silent first; on a
 * genuine silent failure it may fire ONE interactive redirect per browser
 * session — un-awaited, so it still returns null. A null token HALTS the
 * cycle: with JWT_MATCH_MODE=oid the server denies Bearer-less writes, so
 * proceeding would only burn retry_count toward 'failed'. Past that halt the
 * Bearer is unconditional (mirroring the apiFetch.js:39-43 attach pattern).
 * caller_email still rides the body for sync_field_capture, where
 * resolve_principal's fallback reads it (params-then-body); the media
 * endpoints no longer read it for identity at all — per the 2026-08-21 recon
 * upload_media and delete_media derive the caller from the token via
 * resolve_principal, so any caller_email they receive is inert.
 *
 * Three-layer failure model (D-4.I), uniform across types:
 *   LAYER 1 transport — fetch() REJECTS (dead network mid-cycle): the sender
 *     returns the HALT_TRANSPORT sentinel → the WHOLE drain halts, item stays
 *     'pending', retry_count untouched. Next trigger resumes from it.
 *   LAYER 2 HTTP non-2xx: bump retry_count, store the error; >= 3 → 'failed'
 *     (review-gated). Type-specific terminal 404s/409s (see senders) are
 *     routed to their terminal status instead of retrying — and AUTH
 *     statuses never reach the ladder at all: classifyAuthFailure takes
 *     401 → HALT_TOKEN_REFUSED (LAYER 1 semantics) and 403 → terminal 'error'
 *     {error:'auth_denied'}, both with retry_count untouched.
 *   LAYER 3 HTTP 2xx: type-specific parse → markCaptureResult terminal mark.
 */

import { getPendingQueue, markCaptureResult, claimFieldCapture, settleFieldCapture } from './fieldOutbox';
import { getFieldTokenSilent } from './preflightToken';
import { isPendingRecord } from './pendingId';
import { isFieldDrainActive, setFieldDrainActive } from './drainLatch';
import { MAX_RETRIES } from './retryPolicy';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

/**
 * B-035: why a cycle halted, carried on the summary as `haltCause` beside the
 * `halted` boolean (which keeps its meaning). Named for what the rep can act
 * on: no token before the drain started, the server refused the token (any
 * 401), or the network failed (any fetch rejection). Coarser than the truth
 * by design: 'no_token' folds preflightToken's four null arms together, and a
 * 401 body is generic on purpose (principal.py _unauthorized).
 */
export const HALT_CAUSES = Object.freeze({
  NO_TOKEN: 'no_token',
  TOKEN_REFUSED: 'token_refused',
  TRANSPORT: 'transport',
});

// Sender outcomes that halt the cycle, each mapped to its cause. The loop
// stops on any of them; 'no_token' is set by the drain itself, before any
// sender runs.
const HALT_TRANSPORT = 'halt:transport';
const HALT_TOKEN_REFUSED = 'halt:token_refused';
const HALT_CAUSE_OF = {
  [HALT_TRANSPORT]: HALT_CAUSES.TRANSPORT,
  [HALT_TOKEN_REFUSED]: HALT_CAUSES.TOKEN_REFUSED,
};

/** Shared LAYER 2 retry/cap marking. Returns 'retried' | 'failed'. */
async function markHttpFailure(item, res, errBody) {
  const retryCount = (item.retry_count || 0) + 1;
  const nextStatus = retryCount >= MAX_RETRIES ? 'failed' : 'pending';
  await markCaptureResult(item.outbox_id, nextStatus, {
    retry_count: retryCount,
    error: (errBody && errBody.error) || `HTTP ${res.status}`,
  });
  console.warn(`[fieldQueue] HTTP ${res.status} on ${item.outbox_id} → ${nextStatus} (retry ${retryCount})`);
  return nextStatus === 'failed' ? 'failed' : 'retried';
}

/**
 * Shared AUTH classifier — runs BEFORE the LAYER 2 retry ladder in every
 * sender. An auth rejection is not a transient error, so neither status may
 * enter markHttpFailure: three cycles against a refused token would
 * otherwise drive every item in the queue to the terminal 'failed' state,
 * which no UI surface can currently undo.
 *
 *   401 → HALT_TOKEN_REFUSED. The token itself was refused (stale audience,
 *     wrong env, an identity the server cannot match). Retrying with the SAME
 *     token can only fail again, and every remaining item carries that same
 *     header, so the whole cycle stops here, through the same halt handling as
 *     LAYER 1 but with its own cause (see the outcome check in drainFieldQueue).
 *     NOTHING is marked: the item stays 'pending' with retry_count
 *     untouched, exactly as on a transport halt.
 *   403 → terminal 'error' {error:'auth_denied', message}. The token was
 *     valid but the principal is not permitted this write — a provisioning /
 *     authorization fact about the caller, not a transient condition. The
 *     item is held for review instead of retried; retry_count is untouched.
 *     The discriminator rides `error`, not a bespoke flag: `error` is the
 *     key every other terminal arm in this file writes and every UI gate
 *     reads. Writing it also CLEARS any stale 'HTTP <status>' an earlier
 *     retry left there — markCaptureResult MERGES, so a separate flag would
 *     have sat beside the old string instead of replacing it.
 *
 * Body handling is deliberate: ONLY the 403 arm reads res.json(). A null
 * return leaves the response body UNREAD, so each caller's own res.json()
 * before markHttpFailure still works.
 *
 * @returns {Promise<string|'error'|null>} HALT_TOKEN_REFUSED on a 401;
 *   null = not an auth status, and the caller falls through to its own arms.
 */
async function classifyAuthFailure(item, res) {
  if (res.status === 401) {
    console.warn(`[fieldQueue] HTTP 401 on ${item.outbox_id} — token refused; halting drain (stays pending, retry_count untouched)`);
    return HALT_TOKEN_REFUSED;
  }
  if (res.status === 403) {
    const errBody = await res.json().catch(() => ({}));
    await markCaptureResult(item.outbox_id, 'error', {
      error: 'auth_denied',
      message: (errBody && errBody.error) || 'HTTP 403',
    });
    console.error(`[fieldQueue] HTTP 403 on ${item.outbox_id} — authorization denied; terminal error, NOT the retry ladder`);
    return 'error';
  }
  return null;
}

/**
 * field_capture sender — the original 4.3b behavior, verbatim contract:
 * single-record envelope, results[0] status routing. Returns the summary key,
 * or a halt sentinel: HALT_TRANSPORT on transport rejection (LAYER 1),
 * HALT_TOKEN_REFUSED on a 401.
 */
async function sendFieldCapture(item, headers, callerEmail) {
  const body = JSON.stringify({
    site_id: item.site_id,
    caller_email: callerEmail || null, // fallback path reads params then body
    records: [item.payload],
  });

  // LAYER 1 — transport
  let res;
  try {
    res = await fetch(`${API_BASE_URL}/sync_field_capture`, { method: 'POST', headers, body });
  } catch (netErr) {
    console.warn(`[fieldQueue] transport failure — halting drain (${item.outbox_id} stays pending):`, netErr.message);
    return HALT_TRANSPORT;
  }

  // AUTH first — 401/403 never enter the retry ladder
  // (classifyAuthFailure) — then LAYER 2 for the retryable non-2xx
  // remainder (whole-push 400 or 5xx).
  if (!res.ok) {
    const authOutcome = await classifyAuthFailure(item, res);
    if (authOutcome) return authOutcome;
    const errBody = await res.json().catch(() => ({}));
    return markHttpFailure(item, res, errBody);
  }

  // LAYER 3 — 200: route the single record's terminal status
  let parsed = null;
  try {
    parsed = await res.json();
  } catch (parseErr) {
    parsed = null;
  }
  const rec = parsed && Array.isArray(parsed.results) ? parsed.results[0] : null;
  if (!rec) {
    await markCaptureResult(item.outbox_id, 'error', { error: 'HTTP 200 with no results envelope' });
    console.error(`[fieldQueue] 200 with no results envelope on ${item.outbox_id} — marked error`);
    return 'error';
  }

  switch (rec.status) {
    case 'saved':
      // Sole capture point for the minted id-maps — 4.3c reconciles from here.
      await markCaptureResult(item.outbox_id, 'saved', {
        _etag: rec._etag,
        updated_at: rec.updated_at,
        part_number_ids: rec.part_number_ids || {},
        asset_tag_ids: rec.asset_tag_ids || {},
        ...(rec.dropped_fields ? { dropped_fields: rec.dropped_fields } : {}),
      });
      return 'saved';
    case 'conflict':
      await markCaptureResult(item.outbox_id, 'conflict', {
        fields: rec.fields || [],
        ...(rec.dropped_fields ? { dropped_fields: rec.dropped_fields } : {}),
      });
      return 'conflict';
    case 'not_found':
      await markCaptureResult(item.outbox_id, 'not_found', {
        ...(rec.dropped_fields ? { dropped_fields: rec.dropped_fields } : {}),
      });
      return 'not_found';
    case 'error':
      await markCaptureResult(item.outbox_id, 'error', {
        error: rec.message || 'server error',
        ...(rec.dropped_fields ? { dropped_fields: rec.dropped_fields } : {}),
      });
      return 'error';
    default:
      await markCaptureResult(item.outbox_id, 'error', {
        error: `unknown result status: ${rec.status}`,
      });
      return 'error';
  }
}

/**
 * media_add sender — upload_media, base64 JSON body carried on the item.
 * 201 fresh and 200 idempotent-hit both mark 'saved'. 404 = the tool wasn't
 * found (server-side site resolution) — investigate, not retry: terminal
 * 'error' {error:'tool_not_found'} with retry_count untouched.
 */
async function sendMediaAdd(item, headers, callerEmail) {
  // D-5.3-P3-3 (5.3 Phase 3a, defensive): a create-mode capture is queued
  // against its tool's TEMP pending-* id. POSTing that would 404 (terminal
  // error) for a tool that simply isn't minted yet. SKIP — leave the item
  // pending and untouched (no send, no retry bump, no mark); Phase 3b's D.4
  // dependent re-key (reconcileConfirmations tool_create txn) rewrites it
  // to the server id, and a later cycle sends it normally.
  if (isPendingRecord(item.tool_id)) {
    console.log(`[fieldQueue] media_add ${item.outbox_id} keyed to temp ${item.tool_id} — skipped this cycle (awaits create re-key)`);
    return 'skipped';
  }

  const body = JSON.stringify({
    tool_id: item.tool_id,
    photo_type: item.photo_type,
    file_data: item.blob_data,
    file_ext: item.file_ext,
    disposal_id: item.disposal_id ?? undefined, // undefined drops out of JSON
    caller_email: callerEmail || null,
    // accepted-but-ignored by the gated backend; sent for contract parity
    uploaded_by: callerEmail || null,
    site_id: item.site_id,
  });

  // LAYER 1 — transport
  let res;
  try {
    res = await fetch(`${API_BASE_URL}/upload_media`, { method: 'POST', headers, body });
  } catch (netErr) {
    console.warn(`[fieldQueue] transport failure — halting drain (${item.outbox_id} stays pending):`, netErr.message);
    return HALT_TRANSPORT;
  }

  // LAYER 2 — terminal 404 (tool not found) keeps precedence, then AUTH
  // (401/403 never enter the retry ladder), then retryable non-2xx.
  if (!res.ok) {
    if (res.status === 404) {
      await markCaptureResult(item.outbox_id, 'error', { error: 'tool_not_found' });
      console.error(`[fieldQueue] upload_media 404 (tool not found) on ${item.outbox_id} — terminal error`);
      return 'error';
    }
    const authOutcome = await classifyAuthFailure(item, res);
    if (authOutcome) return authOutcome;
    const errBody = await res.json().catch(() => ({}));
    return markHttpFailure(item, res, errBody);
  }

  // LAYER 3 — 200/201: capture the created Media reference
  let parsed = null;
  try {
    parsed = await res.json();
  } catch (parseErr) {
    parsed = null;
  }
  if (!parsed) {
    // Upload succeeded (2xx) — orphan-tolerant: mark saved without the doc
    // reference rather than re-uploading a duplicate (offlineEngine :534-541
    // precedent). Re-Load surfaces the real doc.
    await markCaptureResult(item.outbox_id, 'saved', { media_id: null, parse_failed: true });
    console.warn(`[fieldQueue] upload_media 2xx with unparseable body on ${item.outbox_id} — marked saved without doc ref`);
    return 'saved';
  }
  await markCaptureResult(item.outbox_id, 'saved', {
    media_id: parsed.media_id,
    blob_path: parsed.blob_path,
    blob_url: parsed.blob_url,
    idempotent: !!parsed.idempotent,
  });
  return 'saved';
}

/**
 * tool_create sender (5.2b — D-5.2b-B) — POST update_tool's create branch.
 * 201 → 'saved' with {server_tool_id, _etag, created_doc} stashed for the
 * reconcile identity re-key. NOTE: the 201 body is underscore-stripped by
 * the endpoint, so _etag is null today — reconcile stamps etag null and the
 * next re-Load refreshes the basis (flagged spec landmine).
 * 409 (site-scoped duplicate supplier_tool_id, D-5.2b-G) → TERMINAL 'error'
 * — a duplicate will never succeed on retry (never-drop: the item surfaces
 * for human resolution, like not_found). Other non-2xx → retry/cap→failed.
 */
async function sendToolCreate(item, headers, callerEmail) {
  const body = JSON.stringify({
    site_id: item.site_id,
    supplier_tool_id: item.supplier_tool_id,
    oem: item.oem ?? null,
    created_by: callerEmail || item.caller_email || null,
  });

  // LAYER 1 — transport
  let res;
  try {
    res = await fetch(`${API_BASE_URL}/update_tool`, { method: 'POST', headers, body });
  } catch (netErr) {
    console.warn(`[fieldQueue] transport failure — halting drain (${item.outbox_id} stays pending):`, netErr.message);
    return HALT_TRANSPORT;
  }

  // LAYER 2 — terminal 409 (duplicate) keeps precedence, then AUTH
  // (401/403 never enter the retry ladder), then retryable non-2xx.
  if (!res.ok) {
    if (res.status === 409) {
      const errBody = await res.json().catch(() => ({}));
      await markCaptureResult(item.outbox_id, 'error', {
        error: 'duplicate_supplier_tool_id',
        existing_tool_id: errBody.existing_tool_id ?? null,
        message: errBody.message ?? null,
      });
      console.error(`[fieldQueue] update_tool 409 (duplicate supplier_tool_id) on ${item.outbox_id} — terminal error`);
      return 'error';
    }
    const authOutcome = await classifyAuthFailure(item, res);
    if (authOutcome) return authOutcome;
    const errBody = await res.json().catch(() => ({}));
    return markHttpFailure(item, res, errBody);
  }

  // LAYER 3 — 201: capture the created identity for the reconcile re-key
  let parsed = null;
  try {
    parsed = await res.json();
  } catch (parseErr) {
    parsed = null;
  }
  if (!parsed || !parsed.tool_id) {
    // Created but response unusable — orphan-tolerant: mark saved without the
    // identity; reconcile leaves the item for the next re-Load to self-heal.
    await markCaptureResult(item.outbox_id, 'saved', { server_tool_id: null, parse_failed: true });
    console.warn(`[fieldQueue] update_tool 2xx with unusable body on ${item.outbox_id} — saved without identity`);
    return 'saved';
  }
  await markCaptureResult(item.outbox_id, 'saved', {
    server_tool_id: parsed.tool_id,
    _etag: parsed._etag ?? null, // null today — endpoint strips underscores
    created_doc: parsed,
  });
  return 'saved';
}

/**
 * media_remove sender — delete_media by media_id. 404 = doc already gone =
 * goal achieved (D-4.AE): terminal 'saved' {already_absent:true}, never retry.
 */
async function sendMediaRemove(item, headers, callerEmail) {
  const url = `${API_BASE_URL}/delete_media`
    + `?media_id=${encodeURIComponent(item.media_id)}`
    + `&caller_email=${encodeURIComponent(callerEmail || '')}`;

  // LAYER 1 — transport
  let res;
  try {
    res = await fetch(url, { method: 'DELETE', headers });
  } catch (netErr) {
    console.warn(`[fieldQueue] transport failure — halting drain (${item.outbox_id} stays pending):`, netErr.message);
    return HALT_TRANSPORT;
  }

  if (res.ok) {
    await markCaptureResult(item.outbox_id, 'saved', {});
    return 'saved';
  }
  if (res.status === 404) {
    await markCaptureResult(item.outbox_id, 'saved', { already_absent: true });
    console.log(`[fieldQueue] delete_media 404 on ${item.outbox_id} — already absent, goal achieved`);
    return 'saved';
  }
  // AUTH before the retry ladder — 401/403 never enter it.
  const authOutcome = await classifyAuthFailure(item, res);
  if (authOutcome) return authOutcome;
  const errBody = await res.json().catch(() => ({}));
  return markHttpFailure(item, res, errBody);
}

/**
 * @param {string} callerEmail — the author, replayed through the fallback gate
 * @returns {Promise<{saved:number, conflict:number, not_found:number,
 *   error:number, failed:number, retried:number, halted:boolean,
 *   haltCause:(string|null)}>} haltCause is one of HALT_CAUSES when halted,
 *   null otherwise (including every early return).
 */
export async function drainFieldQueue(callerEmail) {
  const summary = { saved: 0, conflict: 0, not_found: 0, error: 0, failed: 0, retried: 0, halted: false, haltCause: null };
  if (isFieldDrainActive()) return summary;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return summary;

  setFieldDrainActive(true);
  try {
    const pending = await getPendingQueue(); // FIFO; ALL pending types (D-4.J)
    if (pending.length === 0) return summary;

    console.log(`[fieldQueue] draining ${pending.length} pending item(s)...`);

    // Preflight once per cycle — silent first, then at most one interactive
    // escalation per browser session; null otherwise (D-4.K seam).
    const token = await getFieldTokenSilent();

    // JWT_MATCH_MODE=oid: the server denies Bearer-less writes, so a
    // token-absent cycle can only collect 403s — and every one of those bumps
    // retry_count toward the terminal 'failed' state (markHttpFailure).
    // Halt instead. Items stay pending with retry_count untouched, exactly as
    // on a transport halt, and the next cycle replays them once a token is
    // available.
    if (!token) {
      summary.halted = true;
      summary.haltCause = HALT_CAUSES.NO_TOKEN;
      console.warn(`[fieldQueue] no token — halting drain (${pending.length} item(s) stay pending, retry_count untouched)`);
      return summary;
    }

    // Bearer is unconditional past the halt — a tokenless cycle never reaches
    // this line, so there is no Bearer-less arm to fall back to.
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

    for (const item of pending) {
      let outcome;
      switch (item.type) {
        case 'field_capture':
          // Same-row race fix (2026-10-28): claim first. An item coalesced away
          // since the list was read is skipped — sending it would apply a value
          // whose confirmation lands on nothing. Its replacement drains next cycle.
          if (!(await claimFieldCapture(item))) {
            outcome = 'superseded';
            break;
          }
          try {
            outcome = await sendFieldCapture(item, headers, callerEmail);
          } finally {
            await settleFieldCapture(item.record_id, outcome);
          }
          break;
        case 'media_add':
          outcome = await sendMediaAdd(item, headers, callerEmail);
          break;
        case 'media_remove':
          outcome = await sendMediaRemove(item, headers, callerEmail);
          break;
        case 'tool_create':
          outcome = await sendToolCreate(item, headers, callerEmail);
          break;
        default:
          // Defensive — no producer mints other types; never send.
          await markCaptureResult(item.outbox_id, 'error', { error: `unknown_type:${item.type}` });
          console.error(`[fieldQueue] unknown item type "${item.type}" on ${item.outbox_id} — marked error, never sent`);
          outcome = 'error';
      }

      const haltCause = HALT_CAUSE_OF[outcome];
      if (haltCause) {
        summary.halted = true;
        summary.haltCause = haltCause;
        return summary;
      }
      if (summary[outcome] !== undefined) summary[outcome] += 1;
    }

    console.log('[fieldQueue] drain complete:', JSON.stringify(summary));
    return summary;
  } finally {
    setFieldDrainActive(false);
  }
}
